import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, Factory, Home, Landmark, MapPin, RefreshCw, Store, Trees,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { loadDiscoveryCities, loadDiscoveryLocalities } from "@/lib/discovery";

const DISCOVERY_IMAGE = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=72";

const CATEGORIES = [
  { label: "Plots & Land", to: "/properties?listing_type=SELL&property_types=LAND%2CPLOT", icon: Trees, featured: true },
  { label: "Apartments", to: "/properties?listing_type=SELL&property_types=FLAT", icon: Building2 },
  { label: "Villas", to: "/properties?listing_type=SELL&property_types=VILLA", icon: Landmark },
  { label: "Houses", to: "/properties?listing_type=SELL&property_types=HOUSE", icon: Home },
  { label: "Commercial", to: "/properties?listing_type=SELL&property_types=COMMERCIAL", icon: Store },
  { label: "Projects", to: "/properties?listing_type=SELL&q=project", icon: Factory },
];

function propertyLabel(value) {
  const count = Number(value || 0);
  return `${count.toLocaleString("en-IN")} ${count === 1 ? "property" : "properties"}`;
}

function localityUrl(locality) {
  const params = new URLSearchParams({
    city: locality.city_query_value || locality.city,
    locality: locality.name,
    property_types: "LAND,PLOT",
    listing_type: "SELL",
  });
  return `/properties?${params.toString()}`;
}

function cityUrl(city) {
  return `/properties?${new URLSearchParams({ city: city.query_value || city.name, listing_type: "SELL" }).toString()}`;
}

export default function HomeDiscoverySections() {
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      loadDiscoveryCities({ refresh: reloadKey > 0 }),
      loadDiscoveryLocalities("patna", { refresh: reloadKey > 0 }),
    ])
      .then(([cityItems, localityItems]) => {
        if (!active) return;
        setCities(cityItems);
        setLocalities(localityItems);
      })
      .catch((requestError) => {
        if (!active) return;
        setCities([]);
        setLocalities([]);
        setError(formatApiError(requestError));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reloadKey]);

  return (
    <>
      <section className="border-b border-slate-200/60 bg-white py-12 sm:py-16" data-testid="home-category-discovery">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-12">
          <div className="mb-7">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-amber-700">Start with the right property type</p>
            <h2 className="mt-1 font-display text-2xl font-black text-slate-950 sm:text-3xl">What are you looking for?</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {CATEGORIES.map(({ label, to, icon: Icon, featured }) => (
              <Link
                key={label}
                to={to}
                className={`group flex min-h-28 flex-col justify-between rounded-lg border p-4 transition hover:-translate-y-0.5 hover:shadow-md ${featured ? "col-span-2 border-emerald-300 bg-emerald-950 text-white sm:col-span-1 lg:min-h-36" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              >
                <Icon className={`h-6 w-6 ${featured ? "text-emerald-300" : "text-amber-700"}`} aria-hidden="true" />
                <span className="mt-5 flex items-center justify-between gap-2 text-sm font-extrabold">
                  {label}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200/60 bg-slate-50 py-12 sm:py-16" data-testid="patna-discovery-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-12">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">Local market discovery</p>
              <h2 className="mt-1 font-display text-2xl font-black text-slate-950 sm:text-3xl">Properties in Patna</h2>
              <p className="mt-1 text-sm text-slate-600">Discover popular areas around Patna.</p>
            </div>
            <Link to="/properties?city=Patna&listing_type=SELL" className="inline-flex items-center gap-1 text-xs font-extrabold text-amber-700 hover:text-amber-900">
              View all areas in Patna <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading && <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4">{[0, 1, 2, 3].map((item) => <div key={item} className="h-52 animate-pulse rounded-lg bg-slate-200" />)}</div>}
          {!loading && error && (
            <div className="mt-7 rounded-xl border border-warning/30 bg-warning/10 p-7 text-center" role="alert">
              <p className="text-sm font-bold text-slate-900">Location discovery could not be loaded.</p>
              <p className="mt-1 text-xs text-slate-600">{error}</p>
              <Button type="button" size="sm" variant="outline" className="mt-4 gap-2" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw className="h-4 w-4" />Retry</Button>
            </div>
          )}
          {!loading && !error && (
            <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {localities.slice(0, 4).map((locality) => (
                <Link key={locality.slug} to={localityUrl(locality)} className="group relative aspect-[4/3] min-h-44 overflow-hidden rounded-lg bg-slate-900 shadow-sm">
                  <img src={locality.hero_image || DISCOVERY_IMAGE} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-75 transition duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/25 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                    <p className="text-base font-black uppercase">{locality.name}</p>
                    <p className="mt-1 text-xs text-slate-200">{propertyLabel(locality.property_count)}</p>
                  </div>
                </Link>
              ))}
              {localities.length === 0 && <div className="col-span-2 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600 lg:col-span-4">Patna localities will appear as discovery records are published.</div>}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-slate-200/60 bg-white py-12 sm:py-16" data-testid="popular-areas-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-[1fr_1.2fr] lg:px-12">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-amber-700">Patna area guide</p>
            <h2 className="mt-1 font-display text-2xl font-black text-slate-950">Popular areas in Patna</h2>
            <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
              {localities.map((locality) => (
                <Link key={locality.slug} to={localityUrl(locality)} className="group flex min-h-12 items-center justify-between gap-4 py-3 text-sm">
                  <span className="font-bold text-slate-900 group-hover:text-amber-800">{locality.name}</span>
                  <span className="flex items-center gap-2 text-xs text-slate-500">{propertyLabel(locality.property_count)}<ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
              {!loading && !error && localities.length === 0 && <p className="py-6 text-sm text-slate-500">No locality records are available yet.</p>}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">Browse India</p>
            <h2 className="mt-1 font-display text-2xl font-black text-slate-950">Explore popular cities</h2>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {cities.map((city) => (
                <Link key={city.slug} to={cityUrl(city)} className="group flex min-h-20 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 hover:border-amber-300 hover:bg-amber-50">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-white text-amber-700 shadow-sm"><MapPin className="h-4 w-4" /></span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-extrabold text-slate-900">{city.name}</span>
                    <span className="block text-[10px] text-slate-500">{propertyLabel(city.property_count)}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
